import {
  Alert,
  AlertDescription,
  Box,
  Button,
  Checkbox,
  HStack,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import {
  Form,
  Formik,
  FormikErrors,
  FormikHelpers,
  useFormikContext,
} from "formik";
import {
  type ChangeEvent,
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import { GhostButton, SolidButton } from "~/src/components/common/Button";
import {
  TablePagination,
  useTableLocalPaginationProps,
} from "~/src/components/common/tables/TablePagination";
import { chakraFactory } from "~/src/lib/chakra-ui";

import {
  POPUP_IMPORT_COLUMNS,
  POPUP_IMPORT_DEFAULT_GROUP_VISIBILITY,
  POPUP_IMPORT_GROUP_LABELS,
  POPUP_IMPORT_IMAGE_SLOT_COUNT,
  POPUP_IMPORT_MIN_VISIBLE_IMAGE_SLOTS,
  POPUP_IMPORT_TOGGLEABLE_GROUP_IDS,
  type PopupImportColumnDef,
  type PopupImportColumnGroupId,
  type PopupImportGroupVisibility,
  defaultVisibleImageSlotCount,
  filterColumnsByImageSlotVisibility,
  getGroupHeaderSpans,
  getPopupImportGroupCheckboxLabel,
  getPopupImportGroupHeaderBg,
  getPopupImportGroupText,
  getVisibleColumns,
} from "../popupImportColumnModel";
import {
  PopupImportResolutionProvider,
  usePopupImportResolution,
} from "../PopupImportResolutionContext";
import {
  remapImportFailureLinesAfterRowDelete,
  remapPopupErrorsAfterRowDelete,
} from "../popupImportServerErrors";
import { PopupImportSessionSourceTagsProvider } from "../PopupImportSessionSourceTagsContext";
import {
  PopupImportFormValues,
  PopupImportRowValues,
  normalizePopupImportFormValues,
  validationSchema,
} from "../validationSchema";

import { PopupImportRow } from "./PopupImportRow";
import { PopupImportSourceIdsSync } from "./PopupImportSourceIdsSync";
import { PopupImportColTh, PopupImportGroupTh } from "./PopupImportTableChrome";
import { PopupImportVerificationSync } from "./PopupImportVerificationSync";

/** Formik `status` while editing import results after a partial server failure. */
export type PopupImportFormStatus = {
  importFailureLines?: string[];
};

type RowBinderProps = {
  popupIndex: number;
  onDeleteRow: (index: number) => void;
  visibleColumns: PopupImportColumnDef[];
};

const PopupImportRowBinder = memo<RowBinderProps>(
  function PopupImportRowBinder({ popupIndex, onDeleteRow, visibleColumns }) {
    const handleDelete = useCallback(() => {
      onDeleteRow(popupIndex);
    }, [onDeleteRow, popupIndex]);

    return (
      <PopupImportRow
        popupIndex={popupIndex}
        onDelete={handleDelete}
        visibleColumns={visibleColumns}
      />
    );
  }
);

const emptyImage = () => ({
  imageUrl: "",
  honeycombCode: "",
  alt: "",
  linkUrl: "",
  isInheritQuery: false,
});

function padRowImages(row: PopupImportRowValues): PopupImportRowValues {
  const imgs = [...row.popupImages];
  while (imgs.length < POPUP_IMPORT_IMAGE_SLOT_COUNT) imgs.push(emptyImage());
  return { ...row, popupImages: imgs };
}

export type Props = {
  siteSlug: string;
  initialPopups?: PopupImportRowValues[];
  /** From parsed CSV: highest 画像N with data; sets default visible image columns. */
  initialCsvMaxImageSlot?: number;
  onSubmit: (
    values: PopupImportFormValues,
    helpers: FormikHelpers<PopupImportFormValues>
  ) => Promise<void>;
};

const StyledSection = chakraFactory(Box, {
  baseStyle: {
    mb: "32px",
  },
});

const STICKY_DELETE_W = "44px";
const STICKY_ROW_W = "52px";

type GroupVisibilityCheckboxProps = {
  groupId: PopupImportColumnGroupId;
  isChecked: boolean;
  onCheckedChange: (id: PopupImportColumnGroupId, checked: boolean) => void;
};

const GroupVisibilityCheckbox = memo(function GroupVisibilityCheckbox({
  groupId,
  isChecked,
  onCheckedChange,
}: GroupVisibilityCheckboxProps) {
  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onCheckedChange(groupId, e.target.checked);
    },
    [groupId, onCheckedChange]
  );

  return (
    <Checkbox size="sm" isChecked={isChecked} onChange={handleChange}>
      {getPopupImportGroupCheckboxLabel(groupId)}
    </Checkbox>
  );
});

type ContentProps = {
  onCancel: () => void;
  initialCsvMaxImageSlot?: number;
  initialPopups?: PopupImportRowValues[];
};

function PopupImportFormContent({
  onCancel,
  initialCsvMaxImageSlot,
  initialPopups,
}: ContentProps) {
  const { clearAllMessages } = usePopupImportResolution();
  const {
    values,
    isSubmitting,
    setValues,
    setErrors,
    setStatus,
    setTouched,
    errors,
    status,
  } = useFormikContext<PopupImportFormValues>();
  const importFailureLines = (status as PopupImportFormStatus | undefined)
    ?.importFailureLines;

  const [groupVisibility, setGroupVisibility] =
    useState<PopupImportGroupVisibility>(() => ({
      ...POPUP_IMPORT_DEFAULT_GROUP_VISIBILITY,
    }));

  const [visibleImageSlotCount, setVisibleImageSlotCount] = useState(() =>
    defaultVisibleImageSlotCount(initialCsvMaxImageSlot)
  );

  useEffect(() => {
    setVisibleImageSlotCount(
      defaultVisibleImageSlotCount(initialCsvMaxImageSlot)
    );
  }, [initialCsvMaxImageSlot, initialPopups]);

  const groupFilteredColumns = useMemo(() => {
    const cols = getVisibleColumns(groupVisibility);
    return cols.length > 0 ? cols : POPUP_IMPORT_COLUMNS;
  }, [groupVisibility]);

  const visibleColumns = useMemo(
    () =>
      filterColumnsByImageSlotVisibility(
        groupFilteredColumns,
        visibleImageSlotCount
      ),
    [groupFilteredColumns, visibleImageSlotCount]
  );

  const groupSpans = useMemo(
    () => getGroupHeaderSpans(visibleColumns),
    [visibleColumns]
  );

  const count = values.popups.length;

  // Keep the default page small: every cell subscribes to the whole Formik
  // state, so each visible row re-renders on every keystroke. Rendering fewer
  // rows per page keeps large imports (e.g. 300+ rows) responsive.
  const { tableLocalPaginationProps } = useTableLocalPaginationProps({
    totalCount: count,
    defaultPerPage: 30,
    clampPageWhenTotalShrinks: true,
  });

  const handleDelete = useCallback(
    (index: number) => {
      clearAllMessages();
      void setValues((prev) => ({
        popups: prev.popups.filter((_, i) => i !== index),
      }));
      setErrors(
        remapPopupErrorsAfterRowDelete(
          errors,
          index
        ) as FormikErrors<PopupImportFormValues>
      );
      const prevStatus = status as PopupImportFormStatus | undefined;
      setStatus({
        ...(typeof status === "object" && status !== null ? status : {}),
        importFailureLines: remapImportFailureLinesAfterRowDelete(
          prevStatus?.importFailureLines,
          index
        ),
      });
      setTouched({});
    },
    [
      clearAllMessages,
      errors,
      setErrors,
      setStatus,
      setTouched,
      setValues,
      status,
    ]
  );

  const visible = values.popups.slice(
    tableLocalPaginationProps.from,
    tableLocalPaginationProps.to
  );

  const hasFieldErrors =
    errors.popups && Array.isArray(errors.popups)
      ? errors.popups.some((e) => e && Object.keys(e as object).length > 0)
      : !!errors.popups;

  const showImportAlert =
    hasFieldErrors || (importFailureLines?.length ?? 0) > 0;

  const handleGroupVisibilityCheckedChange = useCallback(
    (id: PopupImportColumnGroupId, checked: boolean) => {
      setGroupVisibility((prev) => ({ ...prev, [id]: checked }));
    },
    []
  );

  const decrementImageSlots = useCallback(() => {
    setVisibleImageSlotCount((c) =>
      Math.max(POPUP_IMPORT_MIN_VISIBLE_IMAGE_SLOTS, c - 1)
    );
  }, []);

  const incrementImageSlots = useCallback(() => {
    setVisibleImageSlotCount((c) =>
      Math.min(POPUP_IMPORT_IMAGE_SLOT_COUNT, c + 1)
    );
  }, []);

  return (
    <Form>
      <VStack spacing="0" alignItems="stretch">
        {count > 0 && (
          <StyledSection>
            {showImportAlert && (
              <Alert
                status="error"
                variant="left-accent"
                mb="12px"
                borderRadius="4px"
              >
                <VStack alignItems="flex-start" spacing="4px" width="100%">
                  {hasFieldErrors ? (
                    <AlertDescription fontSize="13px">
                      入力内容にエラーがあります。各フィールドを確認してください。
                    </AlertDescription>
                  ) : null}
                  {importFailureLines && importFailureLines.length > 0 ? (
                    <>
                      <AlertDescription fontSize="13px" fontWeight="semibold">
                        {hasFieldErrors
                          ? "また、以下の行でインポート時にエラーが発生しました。"
                          : "インポートに失敗した行があります。内容を確認して再送信してください。"}
                      </AlertDescription>
                      {importFailureLines.map((line, i) => (
                        <AlertDescription key={i} fontSize="13px">
                          {line}
                        </AlertDescription>
                      ))}
                    </>
                  ) : null}
                </VStack>
              </Alert>
            )}

            <Box mb="8px">
              <Text fontSize="14px" fontWeight="bold">
                ポップアップ一覧{" "}
                <Text as="span" color="#718096">
                  ({count}件)
                </Text>
              </Text>
            </Box>

            <Wrap spacing="12px" mb="12px" align="center">
              <Text fontSize="12px" color="#4A5568" fontWeight="semibold">
                列グループの表示:
              </Text>
              {POPUP_IMPORT_TOGGLEABLE_GROUP_IDS.map((id) => (
                <WrapItem key={id}>
                  <GroupVisibilityCheckbox
                    groupId={id}
                    isChecked={groupVisibility[id] !== false}
                    onCheckedChange={handleGroupVisibilityCheckedChange}
                  />
                </WrapItem>
              ))}
            </Wrap>

            <HStack spacing="8px" mb="12px" align="center" flexWrap="wrap">
              <Text fontSize="12px" color="#4A5568" fontWeight="semibold">
                表示する画像の数:
              </Text>
              <Button
                type="button"
                aria-label="画像列を減らす"
                size="xs"
                variant="outline"
                minW="28px"
                px="6px"
                onClick={decrementImageSlots}
                isDisabled={
                  visibleImageSlotCount <= POPUP_IMPORT_MIN_VISIBLE_IMAGE_SLOTS
                }
              >
                −
              </Button>
              <Text
                fontSize="12px"
                color="#2D3748"
                minW="14px"
                textAlign="center"
              >
                {visibleImageSlotCount}
              </Text>
              <Button
                type="button"
                aria-label="画像列を増やす"
                size="xs"
                variant="outline"
                minW="28px"
                px="6px"
                onClick={incrementImageSlots}
                isDisabled={
                  visibleImageSlotCount >= POPUP_IMPORT_IMAGE_SLOT_COUNT
                }
              >
                +
              </Button>
              <Text fontSize="11px" color="#718096">
                （最小{POPUP_IMPORT_MIN_VISIBLE_IMAGE_SLOTS}、最大
                {POPUP_IMPORT_IMAGE_SLOT_COUNT}）
              </Text>
            </HStack>

            <Box mb="8px">
              <TablePagination {...tableLocalPaginationProps} />
            </Box>

            <Box
              overflowX="auto"
              overflowY="visible"
              border="1px solid"
              borderColor="#E2E8F0"
              borderRadius="6px"
            >
              <Box
                as="table"
                sx={{ borderCollapse: "collapse" }}
                w="max-content"
                minW="100%"
              >
                <Box as="thead">
                  <Box as="tr">
                    <Box
                      as="th"
                      rowSpan={2}
                      w={STICKY_DELETE_W}
                      minW={STICKY_DELETE_W}
                      p="6px 4px"
                      borderBottom="1px solid #CBD5E0"
                      position="sticky"
                      left={0}
                      bg="#FAFAFA"
                      zIndex={4}
                    />
                    <Box
                      as="th"
                      rowSpan={2}
                      w={STICKY_ROW_W}
                      minW={STICKY_ROW_W}
                      p="6px 4px"
                      borderBottom="1px solid #CBD5E0"
                      position="sticky"
                      left={STICKY_DELETE_W}
                      bg="#FAFAFA"
                      zIndex={4}
                      verticalAlign="middle"
                      textAlign="center"
                    >
                      {"\u00a0"}
                    </Box>
                    {groupSpans.map((span, idx) => (
                      <PopupImportGroupTh
                        key={`${span.groupId}-${idx}`}
                        colSpan={span.count}
                        borderLeft
                        bg={getPopupImportGroupHeaderBg(span.groupId)}
                        color={getPopupImportGroupText(span.groupId)}
                      >
                        {POPUP_IMPORT_GROUP_LABELS[span.groupId] || "\u00a0"}
                      </PopupImportGroupTh>
                    ))}
                  </Box>

                  <Box as="tr">
                    {visibleColumns.map((col, idx) => {
                      const borderLeft =
                        idx === 0 ||
                        (idx > 0 &&
                          visibleColumns[idx - 1].groupId !== col.groupId);
                      return (
                        <PopupImportColTh
                          key={col.id}
                          borderLeft={borderLeft}
                          bg={getPopupImportGroupHeaderBg(col.groupId)}
                          color={getPopupImportGroupText(col.groupId)}
                        >
                          {col.id === "sp_content_width" ? (
                            <>
                              <Text as="span" display="block">
                                スマートフォン
                              </Text>
                              <Text as="span" display="block">
                                ポップアップの幅
                              </Text>
                            </>
                          ) : (
                            col.headerLabel
                          )}
                        </PopupImportColTh>
                      );
                    })}
                  </Box>
                </Box>
                <Box as="tbody">
                  {visible.map((_, localIdx) => {
                    const absoluteIndex =
                      tableLocalPaginationProps.from + localIdx;
                    const row = values.popups[absoluteIndex];
                    if (!row) return null;
                    return (
                      <PopupImportRowBinder
                        key={row._clientRowId}
                        popupIndex={absoluteIndex}
                        onDeleteRow={handleDelete}
                        visibleColumns={visibleColumns}
                      />
                    );
                  })}
                </Box>
              </Box>
            </Box>

            <Box mt="8px">
              <TablePagination {...tableLocalPaginationProps} />
            </Box>
          </StyledSection>
        )}

        {count === 0 && (
          <Box
            border="2px dashed"
            borderColor="#CBD5E0"
            borderRadius="8px"
            p="40px"
            textAlign="center"
            mb="24px"
          >
            <Text fontSize="14px" color="#718096">
              データがありません。ポップアップ一覧からCSVをアップロードしてください。
            </Text>
          </Box>
        )}

        <HStack justifyContent="flex-end" spacing="16px" mt="8px">
          <GhostButton type="button" onClick={onCancel}>
            戻る
          </GhostButton>
          <SolidButton
            type="submit"
            isDisabled={count === 0 || isSubmitting}
            isLoading={isSubmitting}
          >
            保存
          </SolidButton>
        </HStack>
      </VStack>
    </Form>
  );
}

export const PopupImportForm: FC<Props> = ({
  onSubmit,
  siteSlug,
  initialPopups,
  initialCsvMaxImageSlot,
}) => {
  const navigate = useNavigate();

  const handleCancel = useCallback(() => {
    navigate(`/sites/${siteSlug}/popup_groups`);
  }, [navigate, siteSlug]);

  const handleSubmit = useCallback(
    async (
      values: PopupImportFormValues,
      helpers: FormikHelpers<PopupImportFormValues>
    ) => {
      helpers.setStatus(undefined);
      await onSubmit(values, helpers);
    },
    [onSubmit]
  );

  const initialValues = useMemo<PopupImportFormValues>(
    () => ({
      popups: normalizePopupImportFormValues(initialPopups).map(padRowImages),
    }),
    [initialPopups]
  );

  return (
    <Formik<PopupImportFormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnChange={false}
      validateOnBlur={false}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      <PopupImportSessionSourceTagsProvider>
        <PopupImportResolutionProvider>
          <PopupImportVerificationSync />
          <PopupImportSourceIdsSync />
          <PopupImportFormContent
            onCancel={handleCancel}
            initialCsvMaxImageSlot={initialCsvMaxImageSlot}
            initialPopups={initialPopups}
          />
        </PopupImportResolutionProvider>
      </PopupImportSessionSourceTagsProvider>
    </Formik>
  );
};
